import { z } from 'zod';
import { membershipSchema } from './organizations';
import { pageSchema } from './common';
import type { Permission } from './permissions';

/**
 * The people directory (docs/organizations-design.md section 10).
 *
 * Who somebody may see of the deployment's people -- to find a colleague, name
 * an attendee, or know who verified a declaration. It is not administration:
 * the users list, with roles and status, stays with `user.read` and a lead's
 * `party.members.read`. A directory entry is a name, an address and an
 * organization, for anybody the reader may see at all.
 */

/**
 * How far the directory reaches for a reader.
 *
 * - `all`: every organization's people.
 * - `host`: their own organization's and the client's own staff.
 * - `own`: their own organization's only.
 *
 * The database applies the same reach to accounts and organization names in a
 * member of another organization's requests, so the directory and the names
 * on a record cannot disagree (docs/row-level-security-design.md).
 */
export type DirectoryReach = 'all' | 'host' | 'own';

export const DIRECTORY_REACHES = [
  'all',
  'host',
  'own',
] as const satisfies readonly DirectoryReach[];

/**
 * The reach `permissions` gives a reader. The client's own staff see everyone
 * whatever they hold; for anybody else it is the widest directory permission
 * held.
 */
export function directoryReach(
  member: { isExternal: boolean },
  permissions: readonly Permission[],
): DirectoryReach {
  if (!member.isExternal || permissions.includes('directory.all')) return 'all';
  if (permissions.includes('directory.host')) return 'host';
  return 'own';
}

export const directoryEntrySchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  email: z.string().email(),
  memberOf: membershipSchema,
});
export type DirectoryEntry = z.infer<typeof directoryEntrySchema>;

export const directoryPageSchema = pageSchema(directoryEntrySchema);

export const directoryQuerySchema = z.object({
  /** Matches a name or an address, ignoring case. */
  search: z.string().max(120).optional(),
  /** One organization's people -- among those the reader may see. */
  partyId: z.string().uuid().optional(),
  cursor: z.string().max(512).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type DirectoryQuery = z.infer<typeof directoryQuerySchema>;
